import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, MapPin, Bed, DoorOpen, Users, Filter, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AddRoomModal from './AddRoomModal';
import EditRoomModal from './EditRoomModal';
import PropertyBanksSection from './PropertyBanksSection';
import DocumentsList from './DocumentsList';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { LastSyncedBadge } from './LastSyncedBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetails = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const fetchPropertyDetails = async () => {
    try {
      // Fetch property details
      const propertyResponse = await axios.get(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperty(propertyResponse.data);

      // Fetch rooms for this property with cache-busting timestamp
      const roomsResponse = await axios.get(`${API}/properties/${propertyId}/rooms?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch active bookings for this property to enrich "Booked" cards
      let bookings = [];
      try {
        const bRes = await axios.get(`${API}/bookings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        bookings = (bRes.data || []).filter(
          b => b.property_id === propertyId && ['pending', 'confirmed'].includes(b.status)
        );
      } catch (_) { /* bookings is optional context */ }

      const enriched = roomsResponse.data.map(r => ({
        ...r,
        activeBooking: bookings.find(b => b.room_id === r.id) || null,
      }));
      setRooms(enriched);
      setLastSyncedAt(new Date());
    } catch (error) {
      console.error('❌ Error fetching property details:', error);
      toast({
        title: "Error",
        description: "Failed to fetch property details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPropertyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // Hot refresh on any related mutation
  useDataSync([EVT.TENANT, EVT.ROOM, EVT.BOOKING, EVT.PAYMENT, EVT.EXPENSE, EVT.PROPERTY], () => fetchPropertyDetails());
  useRefetchOnFocus(fetchPropertyDetails);

  const handleDeleteRoom = async () => {
    if (!roomToDelete) return;

    try {
      await axios.delete(`${API}/properties/${propertyId}/rooms/${roomToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      emit(EVT.ROOM, { action: 'deleted', id: roomToDelete.id });

      toast({
        title: "Room Deleted!",
        description: "Room has been deleted successfully"
      });

      setShowDeleteConfirm(false);
      setRoomToDelete(null);
      fetchPropertyDetails();
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Delete room error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete room",
        variant: "destructive"
      });
    }
  };


  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Property not found</p>
        <Button onClick={() => navigate('/manager/property/list')} className="mt-4">
          Back to Properties
        </Button>
      </div>
    );
  }

  // Filter rooms based on selected status.
  // Inclusive matching for "occupied" / "available" / "maintenance" so chips
  // catch all backend variants of the same logical state.
  const filteredRooms = (() => {
    if (statusFilter === 'all') return rooms;
    if (statusFilter === 'occupied')
      return rooms.filter(r => r.status === 'occupied' || r.status === 'fully_occupied' || r.status === 'partially_occupied');
    if (statusFilter === 'fully_occupied')
      return rooms.filter(r => r.status === 'fully_occupied' || r.status === 'occupied');
    if (statusFilter === 'available')
      return rooms.filter(r => r.status === 'available' || r.status === 'vacant');
    if (statusFilter === 'under_maintenance')
      return rooms.filter(r => r.status === 'under_maintenance' || r.status === 'maintenance');
    return rooms.filter(room => room.status === statusFilter);
  })();

  // Calculate counts for each status. The backend auto-derives room.status
  // from active tenant count, so we may receive `fully_occupied` /
  // `partially_occupied` / `occupied` (legacy) interchangeably — count all
  // three under the "occupied" buckets so the chips never read 0 when there
  // ARE matching rooms.
  const statusCounts = {
    all:                rooms.length,
    available:          rooms.filter(r => r.status === 'available' || r.status === 'vacant').length,
    occupied:           rooms.filter(r => r.status === 'occupied' || r.status === 'fully_occupied' || r.status === 'partially_occupied').length,
    fully_occupied:     rooms.filter(r => r.status === 'fully_occupied' || r.status === 'occupied').length,
    partially_occupied: rooms.filter(r => r.status === 'partially_occupied').length,
    notice:             rooms.filter(r => r.status === 'notice').length,
    booked:             rooms.filter(r => r.status === 'booked').length,
    under_maintenance:  rooms.filter(r => r.status === 'under_maintenance' || r.status === 'maintenance').length,
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/manager/property/list')}
            variant="outline"
            size="sm"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Properties
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{property.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <LastSyncedBadge syncedAt={lastSyncedAt} onRefresh={fetchPropertyDetails} refreshing={loading} />
        </div>
      </div>

      {/* Property Info Card */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <MapPin size={20} className="text-blue-600" />
              <span className="font-semibold">Address</span>
            </div>
            <p className="text-gray-900">{property.address}</p>
          </div>

          <div>
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Bed size={20} className="text-green-600" />
              <span className="font-semibold">Total Beds</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{property.total_beds}</p>
          </div>

          <div>
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Users size={20} className="text-orange-600" />
              <span className="font-semibold">Occupied Beds</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{property.occupied_beds}</p>
          </div>

          <div>
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <DoorOpen size={20} className="text-purple-600" />
              <span className="font-semibold">Vacant Beds</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{property.vacant_beds}</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-green-600 h-4 rounded-full transition-all"
                  style={{ width: `${(property.occupied_beds / property.total_beds) * 100}%` }}
                ></div>
              </div>
            </div>
            <span className="text-sm font-medium text-gray-600">
              {Math.round((property.occupied_beds / property.total_beds) * 100)}% Occupied
            </span>
          </div>
        </div>
      </Card>

      {/* Banks & UPI section */}
      <PropertyBanksSection propertyId={propertyId} propertyName={property?.name} token={token} />

      {/* Documents — FSSAI, Fire NOC, Lease deeds, electrical certs, etc.
          Reuses the global Documents Vault component with scope="property"
          so uploads land in the same MongoDB collection and surface in the
          global vault page too. */}
      <Card className="p-6">
        <DocumentsList
          scope="property"
          propertyId={propertyId}
          propertyName={property?.name}
        />
      </Card>

      {/* Rooms Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">
            Rooms ({rooms.length})
          </h2>
          <Button
            onClick={() => setShowAddRoomModal(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            Add Room
          </Button>
        </div>

        {/* Modern Filter Buttons */}
        <div className="mb-6 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Filter size={18} className="text-gray-600" />
            <span className="text-sm font-semibold text-gray-700">Filter by Status</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                statusFilter === 'all'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md shadow-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Rooms
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === 'all' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {statusCounts.all}
              </span>
            </button>

            <button
              onClick={() => setStatusFilter('available')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                statusFilter === 'available'
                  ? 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-md shadow-green-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Available
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === 'available' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {statusCounts.available}
              </span>
            </button>

            <button
              onClick={() => setStatusFilter('partially_occupied')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                statusFilter === 'partially_occupied'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md shadow-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Partially Occupied
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === 'partially_occupied' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {statusCounts.partially_occupied || 0}
              </span>
            </button>

            <button
              onClick={() => setStatusFilter('fully_occupied')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                statusFilter === 'fully_occupied'
                  ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white shadow-md shadow-orange-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Fully Occupied
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === 'fully_occupied' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {statusCounts.fully_occupied || 0}
              </span>
            </button>

            <button
              onClick={() => setStatusFilter('notice')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                statusFilter === 'notice'
                  ? 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white shadow-md shadow-yellow-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Notice Period
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === 'notice' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {statusCounts.notice}
              </span>
            </button>

            <button
              onClick={() => setStatusFilter('booked')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                statusFilter === 'booked'
                  ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-md shadow-purple-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Booked
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === 'booked' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {statusCounts.booked}
              </span>
            </button>

            <button
              onClick={() => setStatusFilter('under_maintenance')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                statusFilter === 'under_maintenance'
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-md shadow-red-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Under Maintenance
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === 'under_maintenance' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {statusCounts.under_maintenance}
              </span>
            </button>
          </div>
        </div>

        {/* Results Info */}
        {statusFilter !== 'all' && (
          <div className="mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <span className="text-sm text-blue-800">
              Showing <span className="font-bold">{filteredRooms.length}</span> {statusFilter.replace('_', ' ')} room{filteredRooms.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={() => setStatusFilter('all')}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
            >
              Clear filter
            </button>
          </div>
        )}

        {rooms.length === 0 ? (
          <Card className="p-12 text-center">
            <DoorOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Rooms Yet</h3>
            <p className="text-gray-600 mb-4">Add rooms to this property to start managing tenants</p>
            <Button
              onClick={() => setShowAddRoomModal(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              Add First Room
            </Button>
          </Card>
        ) : filteredRooms.length === 0 ? (
          <Card className="p-12 text-center">
            <Filter className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Rooms Found</h3>
            <p className="text-gray-600 mb-4">
              No rooms with status "{statusFilter.replace('_', ' ')}"
            </p>
            <Button
              onClick={() => setStatusFilter('all')}
              variant="outline"
            >
              Show All Rooms
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" key={`rooms-grid-${refreshKey}`}>
            {filteredRooms.map((room) => (
              <Card key={room.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
                      {room.room_number}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">Room {room.room_number}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        room.status === 'available' ? 'bg-green-100 text-green-800' :
                        room.status === 'notice' ? 'bg-yellow-100 text-yellow-800' :
                        room.status === 'partially_occupied' ? 'bg-blue-100 text-blue-800' :
                        room.status === 'fully_occupied' ? 'bg-orange-100 text-orange-800' :
                        room.status === 'occupied' ? 'bg-orange-100 text-orange-800' :
                        room.status === 'booked' ? 'bg-purple-100 text-purple-800' :
                        room.status === 'under_maintenance' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {room.status === 'notice' ? '⚠ Notice Period' : 
                         room.status === 'under_maintenance' ? '🔧 Under Maintenance' :
                         room.status === 'booked' ? '📝 Booked' :
                         room.status === 'partially_occupied' ? `🔵 Partially Occupied (${room.occupancy_info || ''})` :
                         room.status === 'fully_occupied' ? `🟠 Fully Occupied (${room.occupancy_info || ''})` :
                         room.status === 'occupied' ? 'Occupied' :
                         room.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {/* Booked card: show holder name + rent + deposit + check-in */}
                  {room.status === 'booked' && room.activeBooking && (
                    <div
                      className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2"
                      data-testid={`booked-card-${room.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-[10px] font-bold">
                          {room.activeBooking.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <p className="font-semibold text-purple-900 text-sm truncate">
                          {room.activeBooking.name}
                        </p>
                        <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wide ${
                          room.activeBooking.status === 'confirmed'
                            ? 'bg-purple-600 text-white'
                            : 'bg-amber-500 text-white'
                        }`}>
                          {room.activeBooking.status === 'confirmed' ? 'BOOKED' : 'PENDING'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-[11px] text-purple-900">
                        <div className="flex justify-between"><span className="text-purple-700">Rent:</span><span className="font-semibold">₹{(room.activeBooking.rent_amount || 0).toLocaleString()}/mo</span></div>
                        <div className="flex justify-between"><span className="text-purple-700">Deposit:</span><span className="font-semibold">₹{(room.activeBooking.deposit_amount || 0).toLocaleString()}</span></div>
                        {room.activeBooking.token_amount > 0 && (
                          <div className="flex justify-between"><span className="text-purple-700">Token:</span><span className="font-semibold">₹{room.activeBooking.token_amount.toLocaleString()}</span></div>
                        )}
                        {room.activeBooking.check_in_date && (
                          <div className="flex justify-between"><span className="text-purple-700">Check-in:</span><span className="font-semibold">{new Date(room.activeBooking.check_in_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span></div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Fallback: status=booked but no active booking record (legacy data) */}
                  {room.status === 'booked' && !room.activeBooking && room.token_amount && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2">
                      <div className="flex justify-between items-center">
                        <span className="text-purple-700 font-medium">Token Amount:</span>
                        <span className="font-bold text-purple-900">₹{room.token_amount}</span>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">Advance booking amount received</p>
                    </div>
                  )}
                  
                  {room.tenants && room.tenants.length > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Rent:</span>
                        <span className="font-semibold text-green-600">
                          ₹{room.tenants.reduce((sum, t) => sum + (t.rent_amount || 0), 0)}/month
                        </span>
                      </div>
                      
                      {/* Security Deposit */}
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Security Deposit:</span>
                          <span className="font-semibold text-blue-600">
                            ₹{room.tenants.reduce((sum, t) => sum + (t.deposit_amount || 0), 0)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {room.tenants.length} tenant{room.tenants.length > 1 ? 's' : ''} • Total collected
                        </p>
                      </div>
                    </>
                  )}
                  
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="text-gray-600">Total Beds:</span>
                    <span className="font-semibold">{room.total_beds}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Occupied:</span>
                    <span className="font-semibold text-orange-600">{room.occupied_beds}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vacant:</span>
                    <span className="font-semibold text-green-600">{room.vacant_beds || (room.total_beds - room.occupied_beds)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Floor:</span>
                    <span className="font-semibold">{room.floor}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-semibold capitalize">{room.room_type}</span>
                  </div>
                </div>

                {/* Tenant Information */}
                {room.tenants && room.tenants.length > 0 ? (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    {room.tenants.map((tenant, index) => (
                      <div key={tenant.id} className={`${index > 0 ? 'pt-3 border-t' : ''}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-medium text-gray-900">{tenant.name}</p>
                            {tenant.status === 'notice' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-medium">
                                Notice
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mb-2">{tenant.phone}</p>
                        
                        {/* Payment Status */}
                        <div className={`p-2 rounded-lg ${
                          tenant.balance_status === 'paid' ? 'bg-green-50' :
                          tenant.balance_status === 'due' ? 'bg-red-50' :
                          'bg-blue-50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Rent: ₹{tenant.rent_amount}</span>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              tenant.balance_status === 'paid' ? 'bg-green-100 text-green-800' :
                              tenant.balance_status === 'due' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {tenant.balance_status === 'paid' ? '✓ Fully Paid' :
                               tenant.balance_status === 'due' ? `Due: ₹${tenant.current_balance}` :
                               `Advance: ₹${tenant.current_balance}`}
                            </span>
                          </div>
                          {tenant.balance_status !== 'paid' && (
                            <div className="text-xs text-gray-600 mt-1">
                              Paid: ₹{tenant.current_month_paid}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t text-center">
                    <p className="text-xs text-gray-500 italic">No tenants</p>
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={() => {
                      setSelectedRoom(room);
                      setShowEditRoomModal(true);
                    }}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => {
                      setRoomToDelete(room);
                      setShowDeleteConfirm(true);
                    }}
                    size="sm"
                    variant="outline"
                    className="flex-1 text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                  >
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Room Modal */}
      <AddRoomModal
        isOpen={showAddRoomModal}
        onClose={() => setShowAddRoomModal(false)}
        propertyId={propertyId}
        token={token}
        onSuccess={() => {
          setShowAddRoomModal(false);
          fetchPropertyDetails();
        }}
      />

      {/* Edit Room Modal */}
      <EditRoomModal
        isOpen={showEditRoomModal}
        onClose={() => {
          setShowEditRoomModal(false);
          setSelectedRoom(null);
        }}
        room={selectedRoom}
        token={token}
        onSuccess={() => {
          setShowEditRoomModal(false);
          setSelectedRoom(null);
          // Force a fresh fetch with a small delay to ensure backend has committed
          setTimeout(() => {
            fetchPropertyDetails();
            // Force React to re-render by updating refresh key
            setRefreshKey(prev => prev + 1);
          }, 300);
        }}
      />

      {/* Delete Room Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-md mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Delete Room?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete Room {roomToDelete?.room_number}? This action cannot be undone.
              {roomToDelete?.tenants?.length > 0 && (
                <span className="block mt-2 text-red-600 font-medium">
                  ⚠️ This room has {roomToDelete.tenants.length} active tenant(s).
                </span>
              )}
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setRoomToDelete(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteRoom}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete Room
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PropertyDetails;
